import React from 'react';
import { Trash2, Plus, Braces, Edit } from 'lucide-react';
import { Drawer, DrawerContent, DrawerHeader, DrawerOverlay } from 'equal-ds-ui';
import DOMPurify from 'dompurify';
import type { NudgeItem, NudgeFormErrors } from '../../../types/nudges';
import type { CreateNudgeFormData } from '../../../services/nudges-service';

const sanitizeInput = (input: string): string => {
  if (!input) return '';

  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  });
};

interface NudgeEditDrawerProps {
  onClose: () => void;

  // Edit-specific
  editingNudge: NudgeItem;

  // Form State
  nudgeForm: CreateNudgeFormData;
  setNudgeForm: (form: CreateNudgeFormData) => void;
  formErrors: NudgeFormErrors;

  // Actions
  onUpdateNudge: () => Promise<void>;
  isUpdating: boolean;

  // JSON state for user targets
  jsonTextState: Record<number, string>;
  setJsonTextState: (state: Record<number, string>) => void;
  jsonValidationErrors: Record<number, string>;
  setJsonValidationErrors: (errors: Record<number, string>) => void;

  // User target operations
  addUserTarget: () => void;
  updateUserTarget: (index: number, field: 'user_id' | 'user_placeholders', value: any) => void;
  removeUserTarget: (index: number) => void;
  getJsonText: (index: number) => string;
  validateAndUpdateJson: (index: number, value: string) => void;
}

export const NudgeEditDrawer: React.FC<NudgeEditDrawerProps> = ({
  editingNudge,
  nudgeForm,
  setNudgeForm,
  formErrors,
  onUpdateNudge,
  isUpdating,
  jsonValidationErrors,
  addUserTarget,
  updateUserTarget,
  removeUserTarget,
  getJsonText,
  validateAndUpdateJson,
}) => {
  return (
    <>
      <DrawerOverlay />
      <Drawer variant="overlay" width="75%">
        <DrawerContent className="equal-ds-root w-full flex flex-col flex-1 h-full bg-white">
          <DrawerHeader className="border-b border-gray-200 px-4 py-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="bg-[#E6F3FB] p-1.5 rounded-md">
                <Edit className="w-5 h-5 text-[#0B334A]" />
              </div>

              <div>
                <h2 className="text-xl font-bold text-gray-900">EDIT NUDGE</h2>
                <p className="text-sm text-gray-700 mt-1">
                  Update nudge details for: {editingNudge.nudge_name}
                </p>
              </div>
            </div>
          </DrawerHeader>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Section 1: Basic Information */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 mb-4 uppercase">
                1. Basic Information
              </h3>

              {/* Nudge Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nudge Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nudgeForm.nudge_name}
                  onChange={(e) => setNudgeForm({ ...nudgeForm, nudge_name: sanitizeInput(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter nudge name"
                />
                {formErrors.nudge_name && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.nudge_name}</p>
                )}
              </div>

              {/* Message Template */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message Template <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={nudgeForm.message_template}
                  onChange={(e) => setNudgeForm({ ...nudgeForm, message_template: sanitizeInput(e.target.value) })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Enter message template with {{placeholders}}"
                />
                {formErrors.message_template && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.message_template}</p>
                )}
              </div>
            </section>

            {/* Section 2: User Targeting */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 mb-4 uppercase">
                2. User Targeting
              </h3>

              {/* Targeting Mode Badge (read-only) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Targeting Mode
                </label>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1.5 text-xs font-semibold rounded-full ${
                    nudgeForm.targeting_mode === 'QUERY_BASED'
                      ? 'bg-[#eef4fe] text-[#315d99]'
                      : 'bg-[#edf8f0] text-[#2f7046]'
                  }`}>
                    {nudgeForm.targeting_mode === 'QUERY_BASED' ? 'QUERY' : 'DIRECT'}
                  </span>
                </div>
              </div>

              {/* User Targets (DIRECT mode) */}
              {nudgeForm.targeting_mode === 'DIRECT_USER_IDS' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Targets <span className="text-red-500">*</span>
                  </label>

                  <div className="space-y-4">
                    {nudgeForm.user_targets?.map((target, index) => (
                      <div key={index} className="p-4 border border-gray-300 rounded-lg space-y-3 bg-gray-50">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-gray-700">User {index + 1}</span>
                          {(nudgeForm.user_targets?.length || 0) > 1 && (
                            <button
                              onClick={() => removeUserTarget(index)}
                              className="text-red-500 hover:text-red-700 p-1 rounded-md hover:bg-red-50 transition"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>

                        {/* User ID */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">
                            User ID <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={target.user_id}
                            onChange={(e) => updateUserTarget(index, 'user_id', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            placeholder="Enter user ID"
                          />
                        </div>

                        {/* User Placeholders */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1 flex items-center gap-2">
                            <Braces size={14} className="text-gray-500" />
                            User Placeholders (JSON)
                          </label>
                          <textarea
                            value={getJsonText(index)}
                            onChange={(e) => validateAndUpdateJson(index, e.target.value)}
                            rows={3}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent resize-none font-mono text-sm bg-white ${
                              jsonValidationErrors[index]
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-blue-500'
                            }`}
                            placeholder='{"name": "John", "amount": "1000"}'
                          />
                          {jsonValidationErrors[index] && (
                            <p className="text-red-500 text-xs mt-1">{jsonValidationErrors[index]}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={addUserTarget}
                    className="mt-3 flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
                  >
                    <Plus size={16} />
                    Add User Target
                  </button>

                  {formErrors.user_targets && (
                    <p className="text-red-500 text-sm mt-2">{formErrors.user_targets}</p>
                  )}
                </div>
              )}

              {/* SQL Query (QUERY_BASED mode) */}
              {nudgeForm.targeting_mode === 'QUERY_BASED' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SQL Query <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={nudgeForm.sql_query}
                    onChange={(e) => setNudgeForm({ ...nudgeForm, sql_query: e.target.value })}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
                    placeholder="SELECT user_id, name, amount FROM users WHERE ..."
                  />
                  {formErrors.sql_query && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.sql_query}</p>
                  )}
                </div>
              )}
            </section>
          </div>

          {/* Sticky footer */}
          <div className="p-4 border-t border-gray-200 sticky bottom-0" style={{ backgroundColor: '#ffffff' }}>
            <button
              onClick={onUpdateNudge}
              disabled={isUpdating}
              className="nudge-action-btn w-full font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isUpdating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Updating Nudge...
                </>
              ) : (
                'Update Nudge'
              )}
            </button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};
